'use client';

import type { PostHog as Posthog_Client } from 'posthog-js';
import type { PostHog_Features } from './shared';
import { PostHog_OVERRIDES } from './shared';
import { PH_FEATURES } from './generated';

/** Where the PostHog client stores the flags the toolbar has forced. */
const POSTHOG_OVERRIDE_PROPERTY = '$override_feature_flags';

export class PostHog_ClientFeaturesSDK {
    static getFlag<T extends PostHog_Features.Name>(
        client: Posthog_Client,
        key: T,
        fallback?: PostHog_Features.Variant<T>,
    ): PostHog_Features.Variant<T> | null {
        const overridden = PostHog_ClientFeaturesSDK.getOverride(client, key);
        if (overridden) return overridden;

        if (!client?.__loaded) {
            return fallback ?? null;
        }

        const flagValue = client.getFeatureFlag(key) as PostHog_Features.Variant<T> | undefined;
        return flagValue ?? fallback ?? null;
    }

    /** The variant someone forced by hand, from the PostHog toolbar or from
     * `PostHog_OVERRIDES` — as opposed to the one PostHog bucketed this visitor
     * into. A caller that otherwise trusts the server's answer has to ask for
     * this separately, or the toolbar switch does nothing. */
    static getOverride<T extends PostHog_Features.Name>(
        client: Posthog_Client,
        key: T,
    ): PostHog_Features.Variant<T> | null {
        if (PostHog_OVERRIDES[key]) return PostHog_OVERRIDES[key];

        const forced: unknown = client?.get_property?.(POSTHOG_OVERRIDE_PROPERTY);
        if (!forced || typeof forced !== 'object') return null;

        const variants: readonly string[] = PH_FEATURES[key];
        const value: unknown = Object.entries(forced).find(([name]) => name === key)?.[1];
        const isVariant = (candidate: unknown): candidate is PostHog_Features.Variant<T> =>
            variants.some(variant => variant === candidate);

        return isVariant(value) ? value : null;
    }
}
